import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Pagination,
  ListItemText
} from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import {
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  LastPage as LastPageIcon,
} from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import CustomButton from '../../CustomButton'
import { useSidebar } from '../../../../contexts/SidebarContext'
import { GLOBAL_CONSTANTS } from '../../../../constants/globalConstants'
import { formatIndianNumber } from '../../../../constants/index.js'
import EditIcon from '../../../../assets/icons/EditIcon.jsx'
dayjs.extend(customParseFormat)

// Constants for better performance and maintainability
const STYLES = {
  container: { width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'left',
    mb: 2,
    pt: 2,
    borderBottom: 'none',
  },
  headerTitle: {
    position: 'relative',
    fontSize: '20px',
    color: 'var(--forbrand-secondary)',
    fontWeight: 500,
  },
  actionsBox: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
  },
  emptyStateBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    border: '1px solid var(--border-subtel)',
    backgroundColor: '#f9fafb',
  },
  emptyStateText: {
    color: 'var(--forbrand-primary)',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  tableContainer: {
    boxShadow: 'none',
    border: '1px solid var(--border-subtel)',
    borderTop: 'none',
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
    '&::-webkit-scrollbar': { display: 'none' },
    '&::-webkit-scrollbar-track': { display: 'none' },
    '&::-webkit-scrollbar-thumb': { display: 'none' },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    marginBottom: '28px',
  },
  table: { minWidth: 1400 },
  tableHead: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerCell: {
    backgroundColor: 'var(--table-header-bg)',
    color: 'var(--forbrand-primary)',
    fontWeight: 600,
    fontSize: '0.875rem',
    borderBottom: '1px solid var(--border-subtel)',
    borderRight: '1px solid var(--border-subtel)',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '12px',
    paddingBottom: '12px',
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    zIndex: 99,
    '&:last-child': { borderRight: 'none' },
  },
  tableRow: {
    backgroundColor: 'white',
    '&:hover': { backgroundColor: 'transparent !important' },
    '&.Mui-selected': { backgroundColor: 'transparent !important' },
    borderBottom: '1px solid var(--border-subtel)',
  },
  rowError: {
    backgroundColor: '#fff5f5 !important',
  },
  tableCell: {
    borderBottom: '1px solid var(--border-subtel)',
    borderRight: '1px solid var(--border-subtel)',
    color: 'var(--forbrand-primary)',
    fontSize: '0.875rem',
    padding: '14px',
    paddingLeft: '8px',
    '&:last-child': { borderRight: 'none' },
  },
  skeletonCell: {
    borderBottom: '1px solid var(--border-subtel)',
    borderRight: '1px solid var(--border-subtel)',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '8px',
    paddingBottom: '8px',
    '&:last-child': { borderRight: 'none' },
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  checkbox: {
    color: 'var(--forbrand-color)',
    padding: 0,
    '&.Mui-checked': { color: 'var(--forbrand-color)' },
  },
  headerCheckbox: {
    color: 'var(--forbrand-color)',
    padding: 0,
    '&.Mui-checked': { color: 'var(--forbrand-color)' },
    '&.MuiCheckbox-indeterminate': { color: 'var(--forbrand-color)' },
  },
  actionButtonsContainer: {
    display: 'flex',
    gap: 1,
    justifyContent: 'center',
  },
  editButton: {
    color: 'var(--forbrand-primary)',
    '&:hover': { backgroundColor: '#ff980015' },
  },
  saveButton: {
    color: '#4caf50',
    '&:hover': { backgroundColor: '#4caf5015' },
  },
  cancelButton: {
    color: 'var(--forbrand-error)',
    '&:hover': { backgroundColor: '#f4433615' },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      height: '28px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '2px 6px',
      height: '24px',
      boxSizing: 'border-box',
    },
    '& .MuiInputBase-root': {
      height: '28px',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
      marginTop: '2px',
    },
  },
  formControl: { minWidth: 120 },
  select: {
    fontSize: '0.875rem',
    height: '28px',
    padding: '0 6px',
    display: 'flex',
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

}

const COMPONENT_NAMES_WITHOUT_HEADER = ['VendorDetails', 'VendorLdcDetails']

// Skeleton components
const SkeletonRow = React.memo(({ columns, showCheckbox }) => (
  <TableRow sx={STYLES.tableRow}>
    {columns.map((column, index) => (
      <TableCell key={`skeleton-${index}`} sx={STYLES.skeletonCell}>
        {column.isCheckboxColumn && showCheckbox ? (
          <Box sx={STYLES.checkboxContainer}>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ) : column.isActionColumn ? (
          <Box sx={STYLES.actionButtonsContainer}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
        ) : (
          <Skeleton
            variant="text"
            width={Math.random() * 40 + 60 + '%'}
            height={20}
          />
        )}
      </TableCell>
    ))}
  </TableRow>
))

const TableSkeleton = React.memo(
  ({ columns, showCheckbox, skeletonRows = 5 }) => (
    <TableBody>
      {Array.from({ length: skeletonRows }).map((_, index) => (
        <SkeletonRow
          key={`skeleton-row-${index}`}
          columns={columns}
          showCheckbox={showCheckbox}
        />
      ))}
    </TableBody>
  )
)

// Memoized components for better performance
const EmptyState = React.memo(
  ({
    title,
    actions,
    componentName,
    showHeader,
    editingRowId,
    onCancelEdit,
  }) => (
    <Box sx={STYLES.container}>
      {showHeader && (
        <Box sx={STYLES.headerBox}>
          <Typography variant="h6" component="h2" sx={STYLES.headerTitle}>
            {title}
          </Typography>
          <ActionButtons
            actions={actions}
            editingRowId={editingRowId}
            onCancelEdit={onCancelEdit}
          />
        </Box>
      )}
      <Box
        sx={{
          ...STYLES.emptyStateBox,
          borderTop: showHeader ? 'none' : '1px solid var(--border-subtel)',
        }}
      >
        <Typography variant="body1" sx={STYLES.emptyStateText}>
          Data not available
        </Typography>
      </Box>
    </Box>
  )
)

const ActionButtons = React.memo(
  ({ actions = [], editingRowId, onCancelEdit }) => (
    <Box sx={STYLES.actionsBox}>
      {actions.map((action, index) => {
        // Special handling for cancel action - show only when in edit mode
        if (
          action.tooltip === 'Cancel' ||
          action.label === 'Cancel' ||
          action.type === 'cancel'
        ) {
          if (editingRowId === null) return null // Hide when not editing
          return (
            <Tooltip key={index} title="Cancel Edit">
              <IconButton
                onClick={onCancelEdit || action.onClick}
                sx={{
                  color: 'var(--forbrand-primary)'
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )
        }

        if (action.label === 'Add' || action.tooltip === 'Add') {
          return (<React.Fragment key={index}>
            <Typography
              // key={index}
              sx={{
                height: '16px',
                borderLeft: '1px solid var(--forbrand-color)',
                flexShrink: 0,
                px: '4px'
              }}
            ></Typography>
            <CustomButton disabled={editingRowId} onClick={action.onClick}>
              Add
            </CustomButton>
          </React.Fragment>
          )
        }

        // Use buttonProps for ref/onClick if provided (for search icon anchoring)
        return (
          <Tooltip key={index} title={action.tooltip || action.label}>
            <IconButton
              {...(action.buttonProps || {})}
              onClick={action.buttonProps?.onClick || action.onClick}
              disabled={action.disabled || false}
              sx={{
                color: action.color || 'var(--forbrand-primary)',

                '&.Mui-disabled': {
                  color: action.disabled ? '#cccccc' : 'inherit',
                },
              }}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        )
      })}
    </Box>
  )
)

const EditableCell = React.memo(
  ({
    column,
    value,
    validationError,
    onEditFieldChange,
    onFieldBlur,
    rowId,
  }) => {
    const handleChange = useCallback(
      newValue => {
        onEditFieldChange(rowId, column.key, newValue)
      },
      [onEditFieldChange, rowId, column.key]
    )

    if (column.type === 'dropdown' && column.options) {
      return (
        <FormControl
          size="small"
          variant="outlined"
          error={!!validationError}
          sx={STYLES.formControl}
        >
          <Select
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            displayEmpty
            sx={STYLES.select}
          >
            {column.options.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {validationError && (
            <FormHelperText sx={{ fontSize: '0.75rem' }}>
              {validationError}
            </FormHelperText>
          )}
        </FormControl>
      )
    }

    if (column.type === 'multiSelect' && column.options) {
      // Convert stored comma-separated string to array for Select
      const safeValue = Array.isArray(value)
        ? value
        : value
          ?.split(',')
          .map(v => v.trim())
          .filter(Boolean) || []




      return (
        <FormControl
          size="small"
          variant="outlined"
          error={!!validationError}
          sx={STYLES.formControl}
        >
          <Select
            multiple
            value={safeValue} // always an array
            onChange={e => {
              const selectedArray = e.target.value
              const selectedString = selectedArray.filter(Boolean).join(',') // convert array to comma-separated string
              handleChange(selectedString)
            }}
            displayEmpty
            sx={STYLES.select}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                  maxHeight: '250px',
                },
              },
            }}
            renderValue={selected =>
              (Array.isArray(selected) ? selected : [])
                .map(val => {
                  const option = column.options.find(o => o.value === val)
                  return option ? option.label : val
                })
                .join(', ')
            }
          >
            {column.options.map((option, index) => (
              <MenuItem key={option.value || index} value={option.value}>
                <Checkbox checked={safeValue.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          {validationError && (
            <FormHelperText sx={{ fontSize: '0.75rem' }}>
              {validationError}
            </FormHelperText>
          )}
        </FormControl>
      )
    }

    // Handle date fields using MUI DatePicker with dd/MM/yyyy format
    const isDateColumn = column.type === 'date' || /date/i.test(column.key)
    if (isDateColumn) {
      const parseToDayjs = val => {
        if (!val || typeof val !== 'string') return null
        let d = dayjs(val, 'DD/MM/YYYY', true)
        if (d.isValid()) return d
        d = dayjs(val, 'DD-MM-YYYY', true)
        if (d.isValid()) return d
        d = dayjs(val, 'YYYY-MM-DD', true)
        if (d.isValid()) return d
        return null
      }

      const dayjsValue = parseToDayjs(value)
      const today = dayjs()
      const tomorrow = dayjs().add(1, 'day')

      let maxDate, minDate

      if (column.key === 'startDate') {
        maxDate = today // Start date: past or today
      } else if (column.key === 'endDate') {
        minDate = tomorrow // End date: must be future
      }

      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjsValue}
            format="DD/MM/YYYY"
            onChange={newVal => {
              const out = newVal && newVal.isValid() ? newVal.format('DD/MM/YYYY') : ''
              handleChange(out)
            }}
            onAccept={newVal => {
              if (typeof onFieldBlur === 'function') {
                const out = newVal && newVal.isValid() ? newVal.format('DD/MM/YYYY') : ''
                onFieldBlur(rowId, column.key, out)
              }
            }}
            maxDate={maxDate}
            minDate={minDate}
            slotProps={{
              textField: {
                size: 'small',
                variant: 'outlined',
                error: !!validationError,
                helperText: validationError,
                placeholder: 'DD/MM/YYYY',
                sx: STYLES.textField,
              },
            }}
          />
        </LocalizationProvider>
      )
    }

    // Non-date fields
    return (
      <TextField
        value={value}
        onChange={e => {
          const newValue = e.target.value
          handleChange(newValue)
        }}
        onBlur={e => {
          if (typeof onFieldBlur === 'function') {
            onFieldBlur(rowId, column.key, e.target.value)
          }
        }}
        size="small"
        variant="outlined"
        error={!!validationError}
        helperText={validationError}
        placeholder={column.placeholder}
        inputProps={{
          maxLength: column.maxLength,
          title: column.tooltip,
        }}
        sx={STYLES.textField}
      />
    )
  }
)

const ActionColumn = React.memo(
  ({ editingRowId, row, actions, onCancelEdit }) => {
    const isTableEditing = editingRowId !== null

    const handleEditClick = useCallback(() => {
      const editAction = actions.find(
        action => action.tooltip === 'Edit' || action.label === 'Edit'
      )
      if (editAction?.onClick) {
        editAction.onClick(row)
      }
    }, [actions, row])

    const handleCancelClick = useCallback(() => {
      if (onCancelEdit) {
        onCancelEdit(row)
      }
    }, [onCancelEdit, row])

    return (
      <Box sx={STYLES.actionButtonsContainer}>
        <Tooltip
          title={isTableEditing ? 'Save All Changes' : 'Edit All Fields'}
        >
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{ color: 'var(--forbrand-primary)' }}
          >
            {isTableEditing ? (
              <SaveIcon fontSize="small" />
            ) : (
              <EditIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        {isTableEditing && (
          <Tooltip title="Cancel Edit">
            <IconButton
              size="small"
              onClick={handleCancelClick}
              sx={STYLES.cancelButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }
)

const CellContent = React.memo(
  ({
    row,
    column,
    editingRowId,
    editingData,
    validationErrors,
    onEditFieldChange,
    onFieldBlur,
    actions,
    onCancelEdit,
  }) => {
    const value =
      editingRowId === row.sectionMasterUuid &&
        editingData[row.sectionMasterUuid]
        ? editingData[row.sectionMasterUuid][column.key]
        : row[column.key]

    // Row is editable if it is the selected row or when table-wide edit mode is active
    const isEditing =
      (editingRowId === row.sectionMasterUuid || editingRowId === '__ALL__') &&
      column.editable
    const validationError =
      validationErrors[`${row.sectionMasterUuid}_${column.key}`]

    if (isEditing) {
      return (
        <EditableCell
          column={column}
          value={value}
          validationError={validationError}
          onEditFieldChange={onEditFieldChange}
          onFieldBlur={onFieldBlur}
          rowId={row.sectionMasterUuid}
        />
      )
    }

    if (column.isActionColumn) {
      return (
        <ActionColumn
          editingRowId={editingRowId}
          row={row}
          actions={actions}
          onCancelEdit={onCancelEdit}
        />
      )
    }

    if (column.render) {
      return column.render(value, row)
    }

    if (column.renderType === 'chip') {
      const color = column.chipColorMap?.[value] || 'default'
      return <Chip label={value} color={color} size="small" />
    }

    if (column.renderType === 'coloredText') {
      const color = column.colorMap?.[value] || 'var(--forbrand-primary)'
      return <span style={{ color, fontWeight: 500 }}>{value}</span>
    }

    // Do not use '-' for empty data in view mode
    return formatIndianNumber(value) ?? ''
  }
)

const TableHeader = React.memo(
  ({
    columns,
    showCheckbox,
    isAllSelected,
    isIndeterminate,
    onSelectAll,
    editingRowId,
    nSaveAll,
    onCancelEdit,
  }) => (
    <TableHead sx={STYLES.tableHead}>
      <TableRow>
        {columns.map(column => (
          <TableCell
            key={column.key}
            sx={{
              ...STYLES.headerCell,
              minWidth: column.minWidth || 'auto',
            }}
          >
            {column.isCheckboxColumn && showCheckbox ? (
              <Box sx={STYLES.checkboxContainer}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={onSelectAll}
                  size="small"
                  sx={STYLES.headerCheckbox}
                />
                <span>{column.label}</span>
              </Box>
            ) : (
              column.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
)

const TableBodyContent = React.memo(
  ({
    data,
    columns,
    selectedRows,
    showCheckbox,
    onRowSelect,
    editingRowId,
    editingData,
    validationErrors,
    onEditFieldChange,
    onFieldBlur,
    actions,
    onCancelEdit,
  }) => {
    const isSelected = useCallback(
      rowId => selectedRows.includes(rowId),
      [selectedRows]
    )

    const getCellAlignment = useCallback(() => 'left', [])

    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length}
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'var(--forbrand-primary)',
              fontStyle: 'italic',
            }}
          >
            No data available
          </TableCell>
        </TableRow>
      )
    }

    return data.map(row => {
      const hasRowError = Object.keys(validationErrors || {}).some(k =>
        k.startsWith(`${row.sectionMasterUuid}_`)
      )
      return (
        <TableRow
          key={row.sectionMasterUuid}
          hover
          selected={isSelected(row.sectionMasterUuid)}
          sx={{
            ...STYLES.tableRow,
            ...(hasRowError ? STYLES.rowError : {}),
          }}
        >
          {columns.map(column => (
            <TableCell
              key={column.key}
              sx={{
                ...STYLES.tableCell,
                textAlign: getCellAlignment(column.key),
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                minWidth: column.maxWidth || 'auto',
                overflow: 'hidden',
              }}
            >
              {column.isCheckboxColumn && showCheckbox ? (
                <Box sx={STYLES.checkboxContainer}>
                  <Checkbox
                    checked={isSelected(row.sectionMasterUuid)}
                    onChange={() => onRowSelect(row.sectionMasterUuid)}
                    size="small"
                    sx={STYLES.checkbox}
                  />
                  <span>
                    <CellContent
                      row={row}
                      column={column}
                      editingRowId={editingRowId}
                      editingData={editingData}
                      validationErrors={validationErrors}
                      onEditFieldChange={onEditFieldChange}
                      onFieldBlur={onFieldBlur}
                      actions={actions}
                      onCancelEdit={onCancelEdit}
                    />
                  </span>
                </Box>
              ) : (
                <CellContent
                  row={row}
                  column={column}
                  editingRowId={editingRowId}
                  editingData={editingData}
                  validationErrors={validationErrors}
                  onEditFieldChange={onEditFieldChange}
                  onFieldBlur={onFieldBlur}
                  actions={actions}
                  onCancelEdit={onCancelEdit}
                />
              )}
            </TableCell>
          ))}
        </TableRow>
      )
    })
  }
)

const TableFooter = ({
  totalItems = 100,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  showItemsPerPage = true,
  showTotalItems = true,
  showPageNumbers = true,
  customFooterContent,
  selectedCount = 0,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const handleFirstPage = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1)
    }
  }

  const handleLastPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(totalPages)
    }
  }

  const handleItemsPerPageChange = event => {
    const newItemsPerPage = event.target.value
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage)
    }
  }

  if (customFooterContent) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderTop: '1px solid var(--border-subtel)',
          backgroundColor: '#fafafa',
        }}
      >
        {customFooterContent}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        padding: '4px',
        borderTop: '1px solid var(--border-subtel)',
        backgroundColor: '#fafafa',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {selectedCount > 0 && (
          <Typography variant="body2" color="var(--forbrand-color)" sx={{ fontWeight: 500 }}>
            {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
          </Typography>
        )}

        {showItemsPerPage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Items per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                sx={{
                  fontSize: '14px',
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 0
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 0
                  }
                }}
              >
                {itemsPerPageOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {showTotalItems && (
          <Typography variant="body2" sx={{ color: "#CF6210" }}>
            {totalItems === 0
              ? '0 of 0'
              : `${endItem} of ${totalItems}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showPageNumbers && totalPages > 1 && (
          <>
            <Tooltip title={GLOBAL_CONSTANTS.FIRST_PAGE}>
              <IconButton
                size="small"
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                sx={{
                  color: currentPage === 1 ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === 1 ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={GLOBAL_CONSTANTS.PREVIOUS_PAGE}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  color: currentPage === 1 ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === 1 ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => onPageChange && onPageChange(page)}
                size="small"
                shape="rounded"
                showFirstButton={false}
                showLastButton={false}
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '12px',
                    minWidth: '28px',
                    height: '28px',
                  },
                }}
              />
            </Box>

            <Tooltip title={GLOBAL_CONSTANTS.NEXT_PAGE}>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  color: currentPage === totalPages ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === totalPages ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={GLOBAL_CONSTANTS.LAST_PAGE}>
              <IconButton
                size="small"
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                sx={{
                  color: currentPage === totalPages ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === totalPages ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  )
}

const ReusableTable = ({
  title,
  columns,
  data,
  actions = [],
  showCheckbox = true,
  maxHeight = 500,
  editingRowId = null,
  editingData = {},
  onEditFieldChange = () => { },
  onFieldBlur = () => { },
  onRowEdit = () => { },
  onCancelEdit = () => { },
  componentName,
  selectedRows = [],
  onSelectedRowsChange = () => { },
  validationErrors = {},
  loading = false,
  skeletonRows = 5,
  // footer
  showFooter = false,
  totalItems = 100,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  showItemsPerPage = true,
  showTotalItems = true,
  showPageNumbers = true,
  customFooterContent,
  stickyFooter = true,
}) => {
  // Early return for empty data
  const { isCollapsed } = useSidebar()
  const showHeader = !COMPONENT_NAMES_WITHOUT_HEADER.includes(componentName)

  if (!columns || columns.length === 0 || (!data && !loading)) {
    return (
      <EmptyState
        title={title}
        actions={actions}
        componentName={componentName}
        showHeader={showHeader}
        editingRowId={editingRowId}
        onCancelEdit={onCancelEdit}
      />
    )
  }

  // Memoized calculations
  const selectionState = useMemo(
    () => ({
      isAllSelected:
        data && data.length > 0 && selectedRows.length === data.length,
      isIndeterminate:
        selectedRows.length > 0 && data && selectedRows.length < data.length,
    }),
    [data, selectedRows.length]
  )

  // Memoized callbacks
  const handleRowSelect = useCallback(
    rowId => {
      const newSelectedRows = selectedRows.includes(rowId)
        ? selectedRows.filter(id => id !== rowId)
        : [...selectedRows, rowId]

      onSelectedRowsChange(newSelectedRows)
    },
    [selectedRows, onSelectedRowsChange]
  )

  const handleSelectAll = useCallback(
    event => {
      if (event.target.checked && data) {
        onSelectedRowsChange(data.map(row => row.sectionMasterUuid ?? row.id))
      } else {
        onSelectedRowsChange([])
      }
    },
    [data, onSelectedRowsChange]
  )

  const tableContainerStyle = useMemo(
    () => ({
      ...STYLES.tableContainer,
      maxHeight: 'calc(100vh - 280px)',
      marginBottom: 0,
      borderBottom: showFooter && stickyFooter ? 'none' : '1px solid var(--border-subtel)',
      flex: 1,
      overflowY: 'auto',
    }),
    [stickyFooter, showFooter]
  )

  return (
    <Box sx={STYLES.container}>
      {showHeader && (
        <Box sx={STYLES.headerBox}>
          <Typography variant="h6" component="h2" sx={STYLES.headerTitle}>
            {title}
          </Typography>
          <ActionButtons
            actions={actions}
            editingRowId={editingRowId}
            onCancelEdit={onCancelEdit}
          />
        </Box>
      )}

      <TableContainer component={Paper} sx={tableContainerStyle}>
        <Table stickyHeader sx={STYLES.table}>
          <TableHeader
            columns={columns}
            showCheckbox={showCheckbox}
            isAllSelected={selectionState.isAllSelected}
            isIndeterminate={selectionState.isIndeterminate}
            onSelectAll={handleSelectAll}
          />
          {loading ? (
            <TableSkeleton
              columns={columns}
              showCheckbox={showCheckbox}
              skeletonRows={skeletonRows}
            />
          ) : (
            <TableBody>
              <TableBodyContent
                data={data || []}
                columns={columns}
                selectedRows={selectedRows}
                showCheckbox={showCheckbox}
                onRowSelect={handleRowSelect}
                editingRowId={editingRowId}
                editingData={editingData}
                validationErrors={validationErrors}
                onEditFieldChange={onEditFieldChange}
                actions={actions}
                onCancelEdit={onCancelEdit}
              />
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <Paper
        sx={{
          border: '1px solid var(--border-subtel)',
          borderTop: 'none',
          boxShadow: 'none',
          position: 'static',
          backgroundColor: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <TableFooter
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
          itemsPerPageOptions={itemsPerPageOptions}
          showItemsPerPage={showItemsPerPage}
          showTotalItems={showTotalItems}
          showPageNumbers={showPageNumbers}
          customFooterContent={customFooterContent}
          selectedCount={selectedRows.length}
        />
      </Paper>
    </Box>
  )
}

export default React.memo(ReusableTable)
